<script setup lang="ts">
import type { InfiniteData } from "@tanstack/vue-query";

import type { ExtendedEventsOverview, PaginatedList } from "~/types/Catalog";

type Props = {
  data?: InfiniteData<PaginatedList<ExtendedEventsOverview>>;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  isSuccess: boolean;
  hasNextPage?: boolean;
  showInCardView: boolean;
  enableAutoscroll: boolean;
  enableNoEventsFoundMessage: boolean;
  enablePagination?: boolean;
  trackingSource: string;
};

const {
  data,
  hasNextPage = false,
  enablePagination = true,
} = defineProps<Props>();

type Emits = {
  (e: "load-more"): void;
};

const emit = defineEmits<Emits>();

const allEvents = computed(
  () => data?.pages.flatMap((paginatedEvents) => paginatedEvents.results) ?? [],
);

const totalCount = computed(() => {
  return data?.pages[0]?.totalCount;
});

const onLoadMore = () => {
  emit("load-more");
};
</script>

<template>
  <div class="site-viewport-container">
    <EventListSkeleton v-if="isLoading" />
    <div
      v-if="isError"
      class="text-platform-danger"
    >
      {{ $t("errors.loadingEvents") }}
    </div>
    <div v-if="isSuccess && data && allEvents?.length">
      <Suspense>
        <template #default>
          <LazyEventCardGrid
            v-if="showInCardView"
            :events="allEvents"
            class="px-4 md:px-0"
          />
          <LazyEventProductsSlider
            v-for="(event, index) in allEvents"
            v-else
            :key="event.id"
            :event-list-index="index"
            :tracking-source="trackingSource"
            :event="event"
            :enable-virtualization="enablePagination || enableAutoscroll"
          />
        </template>
        <template #fallback>
          <EventListSkeleton />
        </template>
      </Suspense>

      <LazyBasePaginatorShowMore
        v-if="enablePagination && hasNextPage && totalCount"
        :amount-shown="allEvents.length"
        :total-count="totalCount"
        :enable-autoscroll="enableAutoscroll"
        :is-fetching="isFetching"
        @load-more="onLoadMore"
      />
    </div>
    <LazyBasePlaceholderNoResults
      v-if="isSuccess && !allEvents.length && enableNoEventsFoundMessage"
      variant="categoryPage"
    />
  </div>
</template>
